<section class="settings-screen" aria-label="Settings screen">
  <header class="settings-header">
    <p class="settings-header__eyebrow">Settings</p>
    <h2>Account & Preferences</h2>
    <p>Manage your profile, security, and communication preferences.</p>
  </header>

  <mat-card class="settings-card settings-card--profile" aria-label="Profile summary">
    <div class="settings-profile-avatar" [class.settings-profile-avatar--image]="userPhotoUrl()">
      @if (userPhotoUrl()) {
        <img [src]="userPhotoUrl()" alt="Profile photo" />
      } @else {
        <span aria-hidden="true">{{ getUserInitials() }}</span>
      }
    </div>
    <div class="settings-profile-meta">
      <h3>{{ userName() }}</h3>
      <p>{{ userEmail() || 'No email available' }}</p>
    </div>
    <button type="button" class="settings-btn" mat-flat-button color="primary" (click)="onOpenProfile()">
      <mat-icon aria-hidden="true">person</mat-icon>
      Edit profile
    </button>
  </mat-card>

  <div class="settings-grid">
    <mat-card class="settings-card" aria-label="Security settings">
      <h3>Security</h3>
      <p>Update credentials and keep account access protected.</p>
      <button type="button" class="settings-btn" mat-stroked-button color="primary" (click)="onOpenChangePassword()">
        <mat-icon aria-hidden="true">lock</mat-icon>
        Change password
      </button>
    </mat-card>

    <mat-card class="settings-card" aria-label="Notifications settings">
      <h3>Notifications</h3>
      <p>Enable desktop alerts so you never miss an incoming call.</p>
      @if (notificationsUnsupported()) {
        <p class="settings-card__hint">Notifications aren’t supported in this browser.</p>
      } @else if (notificationsEnabled()) {
        <p class="settings-card__hint">Desktop notifications are enabled.</p>
      } @else {
        <p class="settings-card__hint">Desktop notifications are currently blocked.</p>
      }
      <button
        type="button"
        class="settings-btn"
        mat-stroked-button
        color="primary"
        [disabled]="notificationsUnsupported()"
        (click)="requestNotifications()"
      >
        <mat-icon aria-hidden="true">notifications</mat-icon>
        @if (canRequestNotifications()) {
          Enable notifications
        } @else if (notificationsEnabled()) {
          Notifications enabled
        } @else {
          Notifications blocked
        }
      </button>
      <p class="settings-card__hint">
        Chat message tone is
        <strong>{{ chatSoundsEnabled() ? 'enabled' : 'muted' }}</strong>.
      </p>
      <div class="settings-slider-group">
        <div class="settings-slider-group__header">
          <span>Message volume</span>
          <strong>{{ chatSoundVolume() }}%</strong>
        </div>
        <input
          class="settings-slider"
          type="range"
          min="0"
          max="100"
          step="1"
          [value]="chatSoundVolume()"
          [disabled]="!chatSoundsEnabled()"
          (input)="onChatSoundVolumeChange($event)"
          aria-label="Chat message volume"
        />
      </div>
      <button
        type="button"
        class="settings-btn"
        mat-stroked-button
        color="primary"
        (click)="onToggleChatSounds()"
      >
        <mat-icon aria-hidden="true">{{ chatSoundsEnabled() ? 'volume_up' : 'volume_off' }}</mat-icon>
        @if (chatSoundsEnabled()) {
          Mute chat tone
        } @else {
          Unmute chat tone
        }
      </button>
    </mat-card>

    <mat-card class="settings-card" aria-label="Call device settings">
      <h3>Call Devices</h3>
      <p>Choose the microphone, speaker, and camera {{ brand.name }} should prefer when joining Daily calls.</p>
      @if (!mediaDevicesSupported()) {
        <p class="settings-card__hint">This browser does not support device selection.</p>
      } @else {
        <p class="settings-card__hint">Grant device access once so browser labels and available hardware can be detected.</p>
        <div class="device-grid">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Microphone</mat-label>
            <mat-select [value]="selectedMicrophoneId()" (valueChange)="onMicrophoneChange($event)">
              @for (device of microphoneDevices(); track device.deviceId) {
                <mat-option [value]="device.deviceId">{{ device.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Speaker</mat-label>
            <mat-select [value]="selectedSpeakerId()" [disabled]="!audioOutputSupported()" (valueChange)="onSpeakerChange($event)">
              @for (device of speakerDevices(); track device.deviceId) {
                <mat-option [value]="device.deviceId">{{ device.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Camera</mat-label>
            <mat-select [value]="selectedCameraId()" (valueChange)="onCameraChange($event)">
              @for (device of cameraDevices(); track device.deviceId) {
                <mat-option [value]="device.deviceId">{{ device.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
        @if (!audioOutputSupported()) {
          <p class="settings-card__hint">Speaker output switching is not supported in this browser, but mic/camera preferences will still work.</p>
        }
        <button
          type="button"
          class="settings-btn"
          mat-stroked-button
          color="primary"
          (click)="enableDeviceAccess()"
        >
          <mat-icon aria-hidden="true">mic</mat-icon>
          Detect devices
        </button>
      }
    </mat-card>

    <mat-card class="settings-card" aria-label="Appearance settings">
      <h3>Appearance</h3>
      <p>Choose your accent theme and display mode.</p>
      <div class="appearance-group">
        <p class="appearance-label">Theme</p>
      <mat-button-toggle-group
        class="theme-toggle"
        name="theme"
        [value]="currentTheme()"
        (valueChange)="onThemeChange($event)"
        aria-label="Theme selection"
      >
        @for (option of themeOptions; track option.key) {
          <mat-button-toggle [value]="option.key">{{ option.label }}</mat-button-toggle>
        }
      </mat-button-toggle-group>
      </div>
      <div class="appearance-group">
        <p class="appearance-label">Mode</p>
        <mat-button-toggle-group
          class="mode-toggle"
          name="mode"
          [value]="currentColorMode()"
          (valueChange)="onColorModeChange($event)"
          aria-label="Color mode selection"
        >
          @for (option of colorModeOptions; track option.key) {
            <mat-button-toggle [value]="option.key">{{ option.label }}</mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>
    </mat-card>

    <!-- <mat-card class="settings-card" aria-label="Privacy settings">
      <h3>Privacy</h3>
      <p>Manage discoverability and data-sharing preferences.</p>
      <button type="button" class="settings-btn" mat-stroked-button color="primary" disabled>
        <mat-icon aria-hidden="true">visibility</mat-icon>
        Privacy controls
      </button>
    </mat-card> -->

    @if (canManageUsers()) {
      <mat-card class="settings-card" aria-label="Management">
        <h3>Management</h3>
        <p>
          @if (canManageCompany()) {
            Manage organization users, roles, and company details.
          } @else {
            Manage organization users and role assignments.
          }
        </p>
        <button type="button" class="settings-btn" mat-stroked-button color="primary" (click)="onOpenManagement()">
          <mat-icon aria-hidden="true">admin_panel_settings</mat-icon>
          @if (canManageCompany()) {
            Manage users & company
          } @else {
            Manage users
          }
        </button>
      </mat-card>
    }
  </div>
</section>
